import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction, Update } from '@reduxjs/toolkit';
import { DeviceMountEnum, DeviceVariantsEnum, RadioRolesEnum } from '@ubnt/link-core/enums';
import { getAllDevices } from '@ubnt/link-core/modules/devices';
import Device from '@ubnt/link-core/modules/devices/Device';
import { mapValues, omit } from 'lodash-es';
import { current, isDraft, WritableDraft } from 'immer';

import { bandLabelGetter } from '@ubnt/link-core-ui/utils/deviceTexts';
import {
  countrySupportsAfc,
  countrySupportsBand,
  countrySupportsChannelWidth,
  countrySupportsFrequency,
  DEFAULT_COUNTRY,
  frequencySupportsChannelWidth,
  getMaxSupportedChannelWidth,
  getSupportedChannel,
  isChannelDFS,
  getBandByVariant,
  getFrequency,
  is6GHzBand,
  is5GHzBandByVariant,
} from '@ubnt/wifi-calculator/utils/regulatoryLimits';
import { deviceInstanceGetter } from '@ubnt/wifi-calculator/device/deviceInstanceGetter';
import { findDevice } from '@ubnt/wifi-calculator/device/findDevice';
import { type CustomClientFormType, createCustomDeviceData } from '@ubnt/wifi-calculator/device/customClients';
import { BUILTIN_ANTENNA_NAME } from '@ubnt/wifi-calculator/device/constants';
import {
  getBaseBandByVariant,
  getSelectableChannels,
  getSupportedVariant,
  deviceSwitch,
  DEFAULT_DEVICE,
  type deviceName,
} from '@ubnt/wifi-calculator/utils';
import type { AppThunk } from 'app/store';
import type { RootState } from 'app/store/reducer';
import { optionalThirdArg, secondArg } from 'app/store/utils';
import { LOCAL_STORAGE_KEYS } from 'app/storageKeys';
import {
  AUTO_CHANNEL,
  AUTO_CHANNEL_LABEL,
  type AUTO_CHANNEL_TYPE,
} from 'modules/bottomPanel/components/elements/ChannelDropdown';
import { DEFAULT_AP_POSITION, DEFAULT_CLIENT_POSITION } from 'modules/editor/constants';
import { settingsCountrySelector, storedSettings } from 'modules/settings/slice';
import { getLocalStorage, setLocalStorage } from 'utils/storage';

import { getMeshVariant, isAp, isRoleAp, throwReducerError, isAfcForcedGetter } from './utils';
import { AP_ID, CLIENT_ID, MESH_AP_ID, DEFAULT_CLIENT } from './constants';

const { Ap, Cpe } = RadioRolesEnum;

export type ApId = typeof AP_ID | typeof MESH_AP_ID;
export type ClientId = typeof CLIENT_ID | typeof MESH_AP_ID;
type Channel = number | AUTO_CHANNEL_TYPE;

interface BaseStateDevice {
  name: string;
  id: string;
  position: { x: number; y: number };
}

export interface ApStateDevice extends BaseStateDevice {
  role: typeof Ap;
  antennaName: string;
  mount: ValuesOf<typeof DeviceMountEnum>;
  isOutdoorMode: boolean;
  isAfcEnabled: boolean;
  isMloEnabled: boolean;
  variant: ValuesOf<typeof DeviceVariantsEnum>;
  variantParams: Partial<
    Record<ValuesOf<typeof DeviceVariantsEnum>, { channel: Channel; channelUsage: number; channelWidth: number }>
  >;
}

export interface ClientStateDevice extends BaseStateDevice {
  role: typeof Cpe;
}

export type StateDevice = ApStateDevice | ClientStateDevice;

type DevicesMapByRole = {
  [AP_ID]: ApStateDevice;
  [MESH_AP_ID]: ApStateDevice;
  [CLIENT_ID]: ClientStateDevice;
};

export type DevicesMap = DevicesMapByRole & Record<string, StateDevice>;

export type ExternalDeviceMap = Record<string, Device>;

const devicesAdapter = createEntityAdapter<StateDevice>();
const emptyInitialState = devicesAdapter.getInitialState();

// Getters
export function devicesDeviceIdGetter(device: ApStateDevice): ApId;
export function devicesDeviceIdGetter(device: ClientStateDevice): ClientId;
export function devicesDeviceIdGetter(device: StateDevice): ApId | ClientId;
export function devicesDeviceIdGetter(device: StateDevice) {
  return device.id as ApId | ClientId;
}

export const devicesDeviceAntennaNameGetter = (device: ApStateDevice) => device.antennaName;
export const devicesDeviceVariantGetter = (device: ApStateDevice) => device.variant;
const devicesDeviceVariantParamsGetter = (device: ApStateDevice, variantArg?: ApStateDevice['variant']) => {
  const variant = variantArg ?? devicesDeviceVariantGetter(device);
  return device.variantParams[variant];
};
export const devicesDeviceChannelGetter = (device: ApStateDevice, variantArg?: ApStateDevice['variant']) => {
  return devicesDeviceVariantParamsGetter(device, variantArg)?.channel;
};
export const devicesDeviceIsChannelAutoGetter = (device: ApStateDevice, variantArg?: ApStateDevice['variant']) => {
  const channel = devicesDeviceChannelGetter(device, variantArg);
  return channel === AUTO_CHANNEL;
};
export const devicesDeviceChannelUsageGetter = (device: ApStateDevice, variantArg?: ApStateDevice['variant']) => {
  return devicesDeviceVariantParamsGetter(device, variantArg)?.channelUsage;
};
export const devicesDeviceChannelWidthGetter = (device: ApStateDevice, variantArg?: ApStateDevice['variant']) => {
  return devicesDeviceVariantParamsGetter(device, variantArg)?.channelWidth;
};
export const devicesDeviceNameGetter = (device: StateDevice) => device.name;
export const devicesDeviceRoleGetter = (device: StateDevice) => device.role;
export const devicesDeviceMountGetter = (device: ApStateDevice) => device.mount;
export const devicesDeviceIsOutdoorModeGetter = (device: ApStateDevice) => device.isOutdoorMode;
export const devicesDeviceAfcGetter = (device: ApStateDevice) => device.isAfcEnabled;
export const devicesDeviceMloGetter = (device: ApStateDevice) => device.isMloEnabled;
export const devicesDevicePositionGetter = (device: StateDevice) => device.position;

export const devicesDeviceInstanceDefaultChannelGetter = (device: Device, channelWidth?: number) => {
  if (channelWidth == null) {
    return device.defaultChannel;
  }
  return device.defaultChannelByChannelWidth(channelWidth);
};

export const devicesDeviceCombinedChannelGetter = (
  device: ApStateDevice,
  variantArg?: ApStateDevice['variant'],
  channelWidthArg?: number,
  country = DEFAULT_COUNTRY,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const variant = variantArg ?? devicesDeviceVariantGetter(device);
  const stateChannel = devicesDeviceChannelGetter(device, variant);
  if (stateChannel !== AUTO_CHANNEL) {
    return stateChannel;
  }
  const deviceName = devicesDeviceNameGetter(device);
  const deviceInstance = deviceInstanceGetter(deviceName, variant, externalDeviceMap);
  const isAfcEnabled = devicesDeviceAfcGetter(device);
  const channelWidth = channelWidthArg ?? devicesDeviceChannelWidthGetter(device, variant);
  const defaultChannel = devicesDeviceInstanceDefaultChannelGetter(deviceInstance, channelWidth);
  const channelsMap = deviceInstance.channels;
  if (defaultChannel != null && channelsMap && channelWidth != null) {
    const channelData = channelsMap[defaultChannel];
    const channelFrequency = getFrequency(channelData);
    const isOutdoorMode = devicesDeviceIsOutdoorModeGetter(device);
    if (frequencySupportsChannelWidth(country, channelFrequency, channelWidth, isAfcEnabled, isOutdoorMode)) {
      return defaultChannel;
    }
  }
  if (channelsMap && channelWidth) {
    return getSupportedChannel(country, channelsMap, channelWidth, isAfcEnabled);
  }
  return undefined;
};

const getChannelWidthTooltipDescription = (
  disabled: boolean,
  channel: number | 'AUTO_CHANNEL' | undefined,
  isAfcEnabled?: boolean,
  isOutdoorMode?: boolean
) => {
  if (disabled) {
    const base =
      channel ? `Your current channel (${channel}) does not support this width` : 'This channel width is not supported';
    if (isOutdoorMode) {
      return base + ' in outdoor mode.';
    }
    if (isAfcEnabled) {
      return base + ' in AFC (Automatic Frequency Coordination) mode.';
    }
    return base + '.';
  }
  return undefined;
};

const devicesChannelWidthOptionsGetter = (
  device: Device,
  channel?: number | AUTO_CHANNEL_TYPE,
  country: string = DEFAULT_COUNTRY,
  isOutdoorMode?: boolean,
  isAfcEnabled?: boolean,
  variant?: ValuesOf<typeof DeviceVariantsEnum>
) => {
  const channelWidths = device.channelWidths;
  const channelsMap = device.channels;

  if (!channelWidths || !channelsMap || !channel || variant == null) {
    return null;
  }

  const band = getBandByVariant(variant);
  const is6GHz = is6GHzBand(band);
  const isAfcEnabledAnd6GHz = isAfcEnabled && is6GHz;
  const channelValue = channel === AUTO_CHANNEL ? AUTO_CHANNEL : channelsMap[channel];

  return (
    channelWidths
      // not passing isOutdoorMode or isAfcEnabled here to prevent channel widths from appearing or disappearing on the outdoor/afc toggle
      .filter((channelWidth) => countrySupportsChannelWidth(country, band, channelWidth))
      .map((channelWidth) => {
        const channelWidthString = channelWidth.toString();
        const hasSupportedChannel = !!getSupportedChannel(
          country,
          channelsMap,
          channelWidth,
          isAfcEnabled,
          isOutdoorMode
        );

        if (channelValue === AUTO_CHANNEL) {
          return {
            label: channelWidthString,
            value: channelWidthString,
            isDisabled: !hasSupportedChannel,
            tooltipDescription: getChannelWidthTooltipDescription(
              !hasSupportedChannel,
              undefined,
              isAfcEnabledAnd6GHz,
              isOutdoorMode
            ),
          };
        }

        const frequency = getFrequency(channelValue);
        const isDisabled =
          !hasSupportedChannel ||
          (typeof channelValue !== 'number' && !channelValue.channel_widths.includes(channelWidth)) ||
          !frequencySupportsChannelWidth(country, frequency, channelWidth, isAfcEnabled, isOutdoorMode);

        return {
          label: channelWidthString,
          value: channelWidthString,
          isDisabled,
          tooltipDescription: getChannelWidthTooltipDescription(
            isDisabled,
            !hasSupportedChannel || isAfcEnabledAnd6GHz ? undefined : channel,
            isAfcEnabledAnd6GHz,
            isOutdoorMode
          ),
        };
      })
  );
};

const getChannelTooltipDescription = (
  disabled: boolean,
  channelWidth: number,
  isDisabledInOutdoorModeOnly: boolean
) => {
  if (!disabled) {
    return undefined;
  }

  return isDisabledInOutdoorModeOnly ?
      'This channel is not supported in outdoor mode.'
    : `This channel does not support ${channelWidth} MHz width.`;
};

const devicesChannelsOptionsGetter = (
  isOutdoorMode: boolean,
  device: Device,
  channelWidth?: number,
  country: string = DEFAULT_COUNTRY,
  isAfcEnabled?: boolean,
  isSixBandActive?: boolean,
  variant?: ValuesOf<typeof DeviceVariantsEnum>
) => {
  if (isAfcEnabled && isSixBandActive) {
    return [{ channel: AUTO_CHANNEL_LABEL }];
  }

  if (variant == null) {
    return [];
  }

  const channelsMap = getSelectableChannels(getBandByVariant(variant), device.channels);

  if (!channelsMap || !channelWidth) {
    return [];
  }

  const band = getBaseBandByVariant(variant);

  return Object.keys(channelsMap)
    .filter((channel) => {
      const channelValue = channelsMap[Number(channel)];
      const frequency = getFrequency(channelValue);
      return countrySupportsFrequency(country, band, frequency);
    })
    .map((channel) => {
      const channelValue = channelsMap[Number(channel)];
      const frequency = getFrequency(channelValue);

      const disabled =
        (typeof channelValue !== 'number' && !channelValue.channel_widths.includes(channelWidth)) ||
        !frequencySupportsChannelWidth(country, frequency, channelWidth, isAfcEnabled, isOutdoorMode);

      const isEnabledIndoor =
        disabled && isOutdoorMode && frequencySupportsChannelWidth(country, frequency, channelWidth, isAfcEnabled);

      return {
        channel,
        disabled,
        isDFS: isChannelDFS(country, frequency, channelWidth),
        tooltipDescription: getChannelTooltipDescription(disabled, channelWidth, isOutdoorMode && isEnabledIndoor),
      };
    });
};

export const devicesDeviceAntennaGetter = (
  stateDevice: ApStateDevice,
  variantArg?: ApStateDevice['variant'],
  externalDeviceMap?: ExternalDeviceMap
) => {
  const variant = variantArg ?? devicesDeviceVariantGetter(stateDevice);
  const deviceName = devicesDeviceNameGetter(stateDevice);
  const device = deviceInstanceGetter(deviceName, variant, externalDeviceMap);
  const antennaName = devicesDeviceAntennaNameGetter(stateDevice);

  return device.antennas.find((antenna) => antenna.name === antennaName) ?? device.antenna;
};

export const getChannel = (
  deviceInstance: Device,
  stateChannel: Channel | undefined,
  channelWidth: number | undefined
) => {
  const defaultChannel = devicesDeviceInstanceDefaultChannelGetter(deviceInstance, channelWidth);
  return stateChannel === AUTO_CHANNEL ? defaultChannel : stateChannel;
};

export const devicesDeviceFrequencyGetter = (
  stateApDevice: ApStateDevice,
  variantArg?: ApStateDevice['variant'],
  country = DEFAULT_COUNTRY,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const variant = variantArg ?? devicesDeviceVariantGetter(stateApDevice);
  const deviceName = devicesDeviceNameGetter(stateApDevice);
  const apDevice = deviceInstanceGetter(deviceName, variant, externalDeviceMap);
  const isAfcEnabled = devicesDeviceAfcGetter(stateApDevice);
  const isOutdoorMode = devicesDeviceIsOutdoorModeGetter(stateApDevice);
  const stateChannel = devicesDeviceChannelGetter(stateApDevice, variant);
  const channelWidth = devicesDeviceChannelWidthGetter(stateApDevice, variant);
  const channelsMap = apDevice.channels;
  const channel = getChannel(apDevice, stateChannel, channelWidth);
  if (channel && channelsMap && channelWidth != null) {
    const channelData = channelsMap[channel];
    const channelFrequency = getFrequency(channelData);
    if (frequencySupportsChannelWidth(country, channelFrequency, channelWidth, isAfcEnabled, isOutdoorMode)) {
      return channelFrequency;
    }
  }
  if (channelsMap && channelWidth != null) {
    const supportedChannel = getSupportedChannel(country, channelsMap, channelWidth, isAfcEnabled);
    return getFrequency(channelsMap[supportedChannel]);
  }
  return undefined;
};

const devicesMeshApExistsGetter = (devicesMap: DevicesMap) => !!devicesMap[MESH_AP_ID];

const devicesApIdsGetter = (devicesMap: DevicesMap): ApId[] => {
  const apIds: ApId[] = [];
  Object.keys(devicesMap).forEach((deviceId) => {
    const device = devicesMap[deviceId];
    if (isAp(device)) {
      apIds.push(deviceId as ApId);
    }
  });
  return apIds;
};

export const devicesMeshApChannelGetter = (devicesMap: DevicesMap) => {
  const apDevice = devicesMap[AP_ID];
  const apName = devicesDeviceNameGetter(apDevice);
  const variant = devicesDeviceVariantGetter(apDevice);
  const selectedMeshVariant = is5GHzBandByVariant(variant) ? variant : undefined;
  const meshVariant = selectedMeshVariant ?? getMeshVariant(deviceInstanceGetter(apName).variants);
  const channel = devicesDeviceChannelGetter(apDevice, meshVariant);
  const channelWidth = devicesDeviceChannelWidthGetter(apDevice, meshVariant);
  const apDeviceInstance = deviceInstanceGetter(apName, meshVariant);

  return getChannel(apDeviceInstance, channel, channelWidth);
};

export const devicesMeshDeviceVariantGetter = (devicesMap: DevicesMap) => {
  const meshDevice = devicesMap[MESH_AP_ID];
  if (!meshDevice) {
    return devicesMap[AP_ID].variant;
  }
  const meshDeviceInstance = deviceInstanceGetter(devicesDeviceNameGetter(meshDevice));

  return getMeshVariant(meshDeviceInstance.variants, devicesMeshApChannelGetter(devicesMap));
};

export const devicesCustomClientDeviceInstanceMapGetter = (customDevices: Record<string, LinkCore.DeviceData>) => {
  const deviceDataMap = isDraft(customDevices) ? current(customDevices) : customDevices;
  return mapValues(deviceDataMap, (deviceData) => new Device(deviceData));
};

export const devicesUsedNamesGetter = (customDevices: Record<string, LinkCore.DeviceData>) =>
  getAllDevices()
    .map((device) => device.name)
    .concat(Object.keys(customDevices));

export const getInitialApDeviceState = (
  id: ApId,
  name: string,
  clientDeviceName: string,
  previousDevice?: ApStateDevice,
  country = DEFAULT_COUNTRY,
  externalDeviceMap?: ExternalDeviceMap
): ApStateDevice => {
  const defaultDevice = findDevice({ name }, externalDeviceMap) as Device;

  const deviceVariantTuples: [Device, ValuesOf<typeof DeviceVariantsEnum>][] = [];
  Object.values(DeviceVariantsEnum).forEach((variant) => {
    const device = findDevice({ name: defaultDevice.getVariantDeviceName(variant) }, externalDeviceMap) as Device;
    if (device) {
      deviceVariantTuples.push([device, variant]);
    }
  });

  let position: { x: number; y: number } = DEFAULT_AP_POSITION;
  let isAfcEnabled = false;
  let isOutdoorMode = false;
  let isMloEnabled = false;
  const clientDeviceInstance = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);
  let variant =
    getSupportedVariant(
      defaultDevice,
      clientDeviceInstance,
      country,
      previousDevice?.isOutdoorMode && defaultDevice.supportsOutdoorMode,
      previousDevice?.isAfcEnabled && defaultDevice.supportsAfc
    ) ?? defaultDevice.variants[0];

  if (previousDevice) {
    position = previousDevice.position;

    const previousVariant = previousDevice.variant;
    if (previousVariant != null && defaultDevice.variants.includes(previousVariant)) {
      variant = previousVariant;
    }
    const band = getBandByVariant(variant);

    if (isAfcEnabled !== previousDevice.isAfcEnabled || previousDevice.isOutdoorMode) {
      isAfcEnabled =
        previousDevice.isAfcEnabled || previousDevice.isOutdoorMode ?
          defaultDevice.supportsAfc && countrySupportsAfc(country)
        : false;
    }

    if (isOutdoorMode !== previousDevice.isOutdoorMode) {
      isOutdoorMode =
        previousDevice.isOutdoorMode ?
          defaultDevice.supportsOutdoorMode && countrySupportsBand(country, band, true, isAfcEnabled)
        : false;
    }

    if (isMloEnabled !== previousDevice.isMloEnabled) {
      isMloEnabled =
        previousDevice.isMloEnabled ?
          defaultDevice.mloVariants != null && clientDeviceInstance.mloVariants != null
        : false;
    }
  }

  const variantParams: ApStateDevice['variantParams'] = deviceVariantTuples.reduce(
    (acc, [device, variant]) => {
      const band = getBandByVariant(variant);
      const previousDeviceParams = previousDevice?.variantParams?.[variant];

      const previousChannelWidth = previousDeviceParams?.channelWidth;
      const usePreviousChannelWidth =
        previousChannelWidth != null &&
        device.channelWidths.includes(previousChannelWidth) &&
        countrySupportsChannelWidth(country, band, previousChannelWidth, isOutdoorMode, isAfcEnabled);
      const useDefaultChannelWidth =
        !countrySupportsBand(country, band, isOutdoorMode, isAfcEnabled) ||
        countrySupportsChannelWidth(country, band, device.defaultChannelWidth!, isOutdoorMode, isAfcEnabled);
      const channelWidth =
        usePreviousChannelWidth ? previousChannelWidth
        : useDefaultChannelWidth ? device.defaultChannelWidth!
        : getMaxSupportedChannelWidth(country, band, isOutdoorMode);

      const previousDeviceChannel = previousDeviceParams?.channel;
      let channel: number | AUTO_CHANNEL_TYPE;
      if (previousDeviceChannel === AUTO_CHANNEL) {
        channel = AUTO_CHANNEL;
      } else {
        const previousDeviceChannelData =
          previousDeviceChannel ? defaultDevice.channels?.[previousDeviceChannel] : undefined;
        const previousDeviceFrequency = previousDeviceChannelData ? getFrequency(previousDeviceChannelData) : undefined;
        channel =
          (
            usePreviousChannelWidth &&
            previousDeviceChannel &&
            previousDeviceFrequency &&
            frequencySupportsChannelWidth(country, previousDeviceFrequency, channelWidth, defaultDevice?.supportsAfc)
          ) ?
            previousDeviceChannel
          : AUTO_CHANNEL;
      }

      if (!previousDeviceParams && channel === AUTO_CHANNEL) {
        // if previousDevice was with low / high bands, we may need to adjust channel if switched to full band
        const channelValue = getChannel(device, channel, channelWidth);
        const channelsMap = device.channels;
        if (
          channelsMap &&
          channelValue &&
          countrySupportsBand(country, band, isOutdoorMode, isAfcEnabled) &&
          !frequencySupportsChannelWidth(
            country,
            getFrequency(channelsMap[channelValue]),
            channelWidth,
            isAfcEnabled,
            isOutdoorMode
          )
        ) {
          channel = getSupportedChannel(country, channelsMap, channelWidth, isAfcEnabled, isOutdoorMode);
        }
      }

      const channelUsage = previousDeviceParams?.channelUsage ?? 0;
      acc[variant] = { channelWidth, channel: channel!, channelUsage };
      return acc;
    },
    {} as Required<ApStateDevice>['variantParams']
  );

  return {
    name,
    id,
    role: Ap,
    antennaName: defaultDevice.antenna.name ?? BUILTIN_ANTENNA_NAME,
    mount: defaultDevice.mount?.[0] ?? DeviceMountEnum.Ceiling,
    position,
    isOutdoorMode,
    isAfcEnabled: isAfcEnabled || isAfcForcedGetter(defaultDevice),
    isMloEnabled,
    variant,
    variantParams,
  };
};

interface DevicesState {
  customDevicesData: Record<string, LinkCore.DeviceData>;
  devices: EntityState<StateDevice, string>;
  isCustomClientFormOpen: boolean;
  editDeviceName?: string;
}

const initialCustomDevicesData: Record<string, LinkCore.DeviceData> = getLocalStorage(
  LOCAL_STORAGE_KEYS.DEVICES_DATA,
  {}
);

const initialState: DevicesState = {
  customDevicesData: initialCustomDevicesData,
  devices: devicesAdapter.upsertMany(emptyInitialState, {
    [AP_ID]: getInitialApDeviceState(AP_ID, DEFAULT_DEVICE, DEFAULT_CLIENT, undefined, storedSettings.country),
    [CLIENT_ID]: {
      name: DEFAULT_CLIENT,
      id: CLIENT_ID,
      role: Cpe,
      position: DEFAULT_CLIENT_POSITION,
    },
  }),
  isCustomClientFormOpen: false,
};

const reduceChangeClientDevice = (
  state: WritableDraft<DevicesState>,
  id: string,
  name: string,
  externalDeviceMap: ExternalDeviceMap
) => {
  devicesAdapter.updateOne(state.devices, { id, changes: { name } });

  const clientInstance = deviceInstanceGetter(name, undefined, externalDeviceMap);
  const apStateDevice = state.devices.entities[AP_ID] as ApStateDevice;
  const variant = devicesDeviceVariantGetter(apStateDevice);

  // switch to client supported variant if needed
  if (!clientInstance.getVariantDeviceName(variant)) {
    const apInstance = deviceInstanceGetter(devicesDeviceNameGetter(apStateDevice));
    const clientSupportedVariant = getSupportedVariant(
      apInstance,
      clientInstance,
      storedSettings.country,
      devicesDeviceIsOutdoorModeGetter(apStateDevice),
      devicesDeviceAfcGetter(apStateDevice)
    );
    if (clientSupportedVariant) {
      apStateDevice.variant = clientSupportedVariant;
    }
  }
  const mlo = devicesDeviceMloGetter(apStateDevice);
  if (mlo && !clientInstance?.mloVariants) {
    apStateDevice.isMloEnabled = false;
  }

  const apMeshStateDevice = state.devices.entities[MESH_AP_ID] as ApStateDevice | undefined;
  if (apMeshStateDevice) {
    const meshVariant = devicesDeviceVariantGetter(apMeshStateDevice);
    if (!clientInstance?.getVariantDeviceName(meshVariant)) {
      const apInstance = deviceInstanceGetter(devicesDeviceNameGetter(apStateDevice));
      const meshInstance = deviceInstanceGetter(devicesDeviceNameGetter(apMeshStateDevice));
      const clientSupportedVariant = getSupportedVariant(
        meshInstance,
        apInstance,
        storedSettings.country,
        devicesDeviceIsOutdoorModeGetter(apMeshStateDevice),
        devicesDeviceAfcGetter(apMeshStateDevice)
      );
      if (clientSupportedVariant) {
        apMeshStateDevice.variant = clientSupportedVariant;
      }
    }
    const meshMlo = devicesDeviceMloGetter(apMeshStateDevice);
    if (meshMlo && !clientInstance?.mloVariants) {
      apMeshStateDevice.isMloEnabled = false;
    }
  }
};

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    devicesDeviceUpdate(state, action: PayloadAction<Update<StateDevice, string>>) {
      devicesAdapter.updateOne(state.devices, action);
    },
    devicesDeviceAdd(state, action: PayloadAction<StateDevice>) {
      devicesAdapter.addOne(state.devices, action);
    },
    devicesDeviceRemove(state, action: PayloadAction<string>) {
      devicesAdapter.removeOne(state.devices, action);
    },
    devicesDeviceVariantParamsUpdate(
      state,
      action: PayloadAction<{
        id: string;
        variant: ValuesOf<typeof DeviceVariantsEnum>;
        changes: { channel?: number | AUTO_CHANNEL_TYPE; channelUsage?: number; channelWidth?: number };
      }>
    ) {
      const { id, variant, changes } = action.payload;
      const device = state.devices.entities[id];
      if (isAp(device)) {
        const variantParams = devicesDeviceVariantParamsGetter(device, variant);
        if (variantParams) {
          if (changes.channel != null) {
            variantParams.channel = changes.channel;
          }
          if (changes.channelUsage != null) {
            variantParams.channelUsage = changes.channelUsage;
          }
          if (changes.channelWidth != null) {
            variantParams.channelWidth = changes.channelWidth;
          }
        }
      }
    },
    devicesDeviceChange(state, action: PayloadAction<{ id: string; name: string; country?: string }>) {
      const { id, name, country } = action.payload;
      const deviceState = state.devices.entities[id];
      const externalDeviceMap = devicesCustomClientDeviceInstanceMapGetter(state.customDevicesData);
      if (isAp(deviceState)) {
        const clientDeviceName = devicesDeviceNameGetter(state.devices.entities[CLIENT_ID]);
        devicesAdapter.setOne(
          state.devices,
          getInitialApDeviceState(id as ApId, name, clientDeviceName, deviceState, country, externalDeviceMap)
        );
      } else {
        reduceChangeClientDevice(state, id, name, externalDeviceMap);
      }
    },
    devicesMountToggle(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      const stateDevice = state.devices.entities[id];
      if (isAp(stateDevice)) {
        const device = deviceInstanceGetter(devicesDeviceNameGetter(stateDevice));
        const mount = stateDevice.mount === DeviceMountEnum.Ceiling ? DeviceMountEnum.Wall : DeviceMountEnum.Ceiling;
        if (device.mount?.includes(mount)) {
          const changes: Partial<ApStateDevice> = { mount };
          devicesAdapter.updateOne(state.devices, { id, changes });
        } else {
          throwReducerError(`Device ${device.name} does not support ${mount} mount.`);
        }
      }
    },
    devicesOutdoorToggle(state, action: PayloadAction<{ id: string; country: string }>) {
      const { id, country } = action.payload;
      const stateDevice = state.devices.entities[id];
      if (isAp(stateDevice)) {
        const isOutdoorMode = !stateDevice.isOutdoorMode;
        const changes: Partial<ApStateDevice> = { isOutdoorMode };

        if (isOutdoorMode) {
          const variant = devicesDeviceVariantGetter(stateDevice);
          const band = getBandByVariant(variant);
          const device = deviceInstanceGetter(devicesDeviceNameGetter(stateDevice));
          let isAfcEnabled = devicesDeviceAfcGetter(stateDevice);

          if (!isAfcEnabled && device.supportsAfc && countrySupportsAfc(country)) {
            isAfcEnabled = true;
            changes.isAfcEnabled = true;
          }

          if (!device.supportsOutdoorMode || !countrySupportsBand(country, band, true, isAfcEnabled)) {
            throwReducerError(`Device ${device.name} or ${country} does not support outdoor mode.`);
            return;
          }
        }

        devicesAdapter.updateOne(state.devices, { id, changes });
      }
    },
    devicesMloChange(state, action: PayloadAction<{ id: string; mlo: boolean }>) {
      const { id, mlo } = action.payload;
      const stateDevice = state.devices.entities[id];

      if (isAp(stateDevice)) {
        const changes: Partial<ApStateDevice> = { isMloEnabled: mlo };

        if (mlo) {
          const device = deviceInstanceGetter(devicesDeviceNameGetter(stateDevice));
          const mloVariants = device.mloVariants;
          const clientDeviceName = devicesDeviceNameGetter(state.devices.entities[CLIENT_ID]);
          const externalDeviceMap = devicesCustomClientDeviceInstanceMapGetter(state.customDevicesData);
          const client = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);
          if (!mloVariants || !client.mloVariants) {
            throwReducerError(`Device ${device.name} or ${client.name} does not support MLO.`);
            return;
          }
          const variant = devicesDeviceVariantGetter(stateDevice);
          if (variant != null && !mloVariants.includes(variant)) {
            changes.variant = mloVariants[0];
          }
        }
        devicesAdapter.updateOne(state.devices, { id, changes });
      }
    },
    devicesAfcChange(state, action: PayloadAction<{ id: string; afc: boolean; country: string }>) {
      const { id, afc, country } = action.payload;
      const stateDevice = state.devices.entities[id];

      if (isAp(stateDevice)) {
        if (afc) {
          const device = deviceInstanceGetter(devicesDeviceNameGetter(stateDevice));
          if (!device.supportsAfc || !countrySupportsAfc(country)) {
            throwReducerError(`Device ${device.name} or ${country} does not support AFC.`);
            return;
          }
        }

        const changes: Partial<ApStateDevice> = { isAfcEnabled: afc };
        devicesAdapter.updateOne(state.devices, { id, changes });
      }
    },
    devicesCustomClientFormOpenChange(state, action: PayloadAction<boolean>) {
      state.isCustomClientFormOpen = action.payload;
      if (!action.payload) {
        state.editDeviceName = undefined;
      }
    },
    devicesCustomClientFormSetEditDevice(state, action: PayloadAction<string>) {
      state.editDeviceName = action.payload;
    },
    devicesAddCustomClientDevices(state, action: PayloadAction<Record<string, LinkCore.DeviceData>>) {
      state.customDevicesData = { ...state.customDevicesData, ...action.payload };
      setLocalStorage(LOCAL_STORAGE_KEYS.DEVICES_DATA, state.customDevicesData);
    },
    devicesUpdateCustomClientDevices(
      state,
      action: PayloadAction<{ deviceData: Record<string, LinkCore.DeviceData>; deviceName: string }>
    ) {
      const { deviceData, deviceName } = action.payload;
      const previousCustomDeviceName = state.editDeviceName;
      if (previousCustomDeviceName) {
        state.customDevicesData = omit(current(state.customDevicesData), [
          previousCustomDeviceName,
          `${previousCustomDeviceName} (2.4 GHz)`,
          `${previousCustomDeviceName} (6 GHz)`,
        ]);
      }
      if (
        previousCustomDeviceName !== deviceName &&
        state.devices.entities[CLIENT_ID].name === previousCustomDeviceName
      ) {
        state.devices.entities[CLIENT_ID].name = deviceName;
      }
      state.customDevicesData = { ...state.customDevicesData, ...deviceData };
      setLocalStorage(LOCAL_STORAGE_KEYS.DEVICES_DATA, state.customDevicesData);
      state.editDeviceName = undefined;
    },
    devicesRemoveCustomClientDevice(state, action: PayloadAction<string>) {
      const name = action.payload;
      if (devicesDeviceNameGetter(state.devices.entities[CLIENT_ID]) === name) {
        const externalDeviceMap = devicesCustomClientDeviceInstanceMapGetter(state.customDevicesData);
        reduceChangeClientDevice(state, CLIENT_ID, DEFAULT_CLIENT, externalDeviceMap);
      }
      state.customDevicesData = omit(current(state.customDevicesData), [name, `${name} (2.4 GHz)`, `${name} (6 GHz)`]);
      setLocalStorage(LOCAL_STORAGE_KEYS.DEVICES_DATA, state.customDevicesData);
    },
  },
});

export const {
  devicesAddCustomClientDevices,
  devicesDeviceUpdate,
  devicesDeviceAdd,
  devicesDeviceRemove,
  devicesDeviceVariantParamsUpdate,
  devicesDeviceChange,
  devicesMountToggle,
  devicesOutdoorToggle,
  devicesMloChange,
  devicesAfcChange,
  devicesCustomClientFormOpenChange,
  devicesCustomClientFormSetEditDevice,
  devicesUpdateCustomClientDevices,
  devicesRemoveCustomClientDevice,
} = devicesSlice.actions;

export const devicesCreateCustomClientDevice: (formData: Required<CustomClientFormType>) => AppThunk =
  (formData) => (dispatch) => {
    const deviceData = createCustomDeviceData(formData);
    const newCustomClientDevices: Record<string, LinkCore.DeviceData> = {};
    Object.values(deviceData).forEach((value) => {
      newCustomClientDevices[value.name] = value;
    });
    dispatch(devicesAddCustomClientDevices(newCustomClientDevices));
    dispatch(devicesDeviceChange({ id: CLIENT_ID, name: formData.name }));
    dispatch(devicesCustomClientFormOpenChange(false));
  };

export const {
  selectById: devicesDeviceByIdSelector,
  selectEntities: devicesDeviceMapSelectorInternal,
  selectIds: devicesDeviceIdsSelector,
} = devicesAdapter.getSelectors((state: RootState) => state.devices.devices);

export const devicesDeviceMapSelector = (state: RootState): DevicesMap =>
  devicesDeviceMapSelectorInternal(state) as DevicesMap;

// Selectors
export const devicesDeviceNameSelector = (state: RootState, id: string) =>
  devicesDeviceNameGetter(devicesDeviceByIdSelector(state, id));

export const devicesDeviceRoleSelector = (state: RootState, id: string) =>
  devicesDeviceRoleGetter(devicesDeviceByIdSelector(state, id));

export const devicesDeviceMountSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceMountGetter(device) : undefined;
};

export const devicesDeviceIsOutdoorModeSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceIsOutdoorModeGetter(device) : undefined;
};

export const devicesDeviceVariantSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceVariantGetter(device) : undefined;
};

export const devicesDeviceIsSixBandActiveSelector = createSelector(devicesDeviceVariantSelector, (variant) => {
  if (variant != null) {
    return is6GHzBand(getBandByVariant(variant));
  }
  return false;
});

export const devicesDeviceAntennaNameSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceAntennaNameGetter(device) : undefined;
};

export const devicesDeviceStateChannelSelector = (
  state: RootState,
  id: string,
  variant?: ValuesOf<typeof DeviceVariantsEnum>
) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceChannelGetter(device, variant) : undefined;
};

export const devicesDeviceChannelWidthSelector = (
  state: RootState,
  id: string,
  variant?: ValuesOf<typeof DeviceVariantsEnum>
) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceChannelWidthGetter(device, variant) : undefined;
};

export const devicesDeviceChannelUsageSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceChannelUsageGetter(device) : undefined;
};

export const devicesDeviceAfcSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceAfcGetter(device) : undefined;
};

export const devicesDeviceMloSelector = (state: RootState, id: string) => {
  const device = devicesDeviceByIdSelector(state, id);
  return isAp(device) ? devicesDeviceMloGetter(device) : undefined;
};

export const devicesDevicePositionSelector = (state: RootState, id: string) =>
  devicesDevicePositionGetter(devicesDeviceByIdSelector(state, id));

export const devicesCustomClientFormOpenSelector = (state: RootState) => state.devices.isCustomClientFormOpen;
export const devicesCustomClientDevicesDataSelector = (state: RootState) => state.devices.customDevicesData;
export const devicesCustomClientFormEditDeviceSelector = (state: RootState) => state.devices.editDeviceName;

export const devicesCustomClientDeviceInstanceMapSelector = createSelector(
  devicesCustomClientDevicesDataSelector,
  devicesCustomClientDeviceInstanceMapGetter
);

export const devicesDeviceMapWithClientPointerPositionSelector = createSelector(
  devicesDeviceMapSelector,
  secondArg,
  (devicesMap: DevicesMap, position: { x: number; y: number }) =>
    Object.keys(devicesMap).reduce(
      (devices, id) => ({
        ...devices,
        [id]: {
          ...devicesMap[id],
          position: id === CLIENT_ID ? position : devicesMap[id].position,
        },
      }),
      {}
    )
);

export const devicesCustomClientDeviceInstancesSelector = createSelector(
  devicesCustomClientDeviceInstanceMapSelector,
  Object.values<Device>
);

export const devicesDeviceInstanceSelector = createSelector(
  devicesDeviceNameSelector,
  optionalThirdArg<ValuesOf<typeof DeviceVariantsEnum>>,
  devicesCustomClientDeviceInstanceMapSelector,
  deviceInstanceGetter
);

export const devicesDeviceSupportsOutdoorMode = createSelector(
  devicesDeviceInstanceSelector,
  devicesDeviceRoleSelector,
  (device, role) => device.supportsOutdoorMode && isRoleAp(role)
);

const AFC_DEVICE_LIST = deviceSwitch.getAfcList().filter(Boolean) as deviceName[];
const WORLD_WIDE_DEVICE_LIST = deviceSwitch.getWorldWideList().filter(Boolean) as deviceName[];

export const devicesDeviceVisibleHiddenByCountrySelector = createSelector(settingsCountrySelector, (country) =>
  countrySupportsAfc(country) ? AFC_DEVICE_LIST : WORLD_WIDE_DEVICE_LIST
);

export const devicesDeviceImgSrcSelector = (state: RootState, id: string) =>
  devicesDeviceInstanceSelector(state, id).imgSrc;

export const devicesDeviceMloVariantsSelector = (state: RootState, id: string) =>
  devicesDeviceInstanceSelector(state, id).mloVariants;

export const devicesDeviceTooltipDescriptionSelector = (state: RootState, id: string) =>
  devicesDeviceInstanceSelector(state, id).tooltipDescription;

export const devicesDeviceSupportsMloSelector = (state: RootState, id: string) =>
  !!devicesDeviceMloVariantsSelector(state, id);

export const devicesDeviceDefaultInstanceSelector = createSelector(
  devicesDeviceNameSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  (deviceName, externalDeviceMap) => deviceInstanceGetter(deviceName, DeviceVariantsEnum.Default, externalDeviceMap)
);

export const devicesDeviceIsAfcForcedSelector = createSelector(devicesDeviceDefaultInstanceSelector, isAfcForcedGetter);

export const devicesDeviceVariantInstanceSelector = createSelector(
  devicesDeviceNameSelector,
  devicesDeviceVariantSelector,
  optionalThirdArg<ValuesOf<typeof DeviceVariantsEnum>>,
  devicesCustomClientDeviceInstanceMapSelector,
  (deviceName, stateVariant, variantArg, externalDeviceMap) =>
    deviceInstanceGetter(deviceName, variantArg ?? stateVariant, externalDeviceMap)
);

export const devicesDeviceVariantFrequencyLabelSelector = createSelector(
  devicesDeviceVariantInstanceSelector,
  devicesDeviceDefaultInstanceSelector,
  (variantInstance, defaultInstance) => bandLabelGetter(variantInstance, defaultInstance.variantsMap)
);

export const devicesDeviceApIdsSelector = createSelector(devicesDeviceMapSelector, devicesApIdsGetter);

export const devicesMeshDeviceVariantSelector = createSelector(
  devicesDeviceMapSelector,
  devicesMeshDeviceVariantGetter
);

export const devicesMeshApChannelSelector = createSelector(devicesDeviceMapSelector, devicesMeshApChannelGetter);

export const devicesIsAnyApInOutdoorModeSelector = createSelector(
  devicesDeviceApIdsSelector,
  devicesDeviceMapSelector,
  (apIds, devicesMap) =>
    apIds.some((id) => {
      const device = devicesMap[id];
      return isAp(device) && devicesDeviceIsOutdoorModeGetter(device);
    })
);

export const devicesDeviceChannelsOptionsSelector = createSelector(
  devicesIsAnyApInOutdoorModeSelector,
  devicesDeviceVariantInstanceSelector,
  devicesDeviceChannelWidthSelector,
  settingsCountrySelector,
  devicesDeviceAfcSelector,
  devicesDeviceIsSixBandActiveSelector,
  devicesDeviceVariantSelector,
  devicesChannelsOptionsGetter
);

export const devicesDeviceChannelWidthsSelector = createSelector(
  devicesDeviceVariantInstanceSelector,
  devicesDeviceStateChannelSelector,
  settingsCountrySelector,
  devicesDeviceIsOutdoorModeSelector,
  devicesDeviceAfcSelector,
  devicesDeviceVariantSelector,
  devicesChannelWidthOptionsGetter
);

export const devicesDeviceAntennaSelector = createSelector(
  devicesDeviceByIdSelector,
  devicesDeviceVariantSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  (device, variant, externalDeviceMap) => {
    if (isAp(device)) {
      return devicesDeviceAntennaGetter(device, variant, externalDeviceMap);
    }
    return undefined;
  }
);

export const devicesBottomPanelImgSrcSelector = createSelector(
  devicesDeviceImgSrcSelector,
  devicesDeviceAntennaSelector,
  (deviceImg, antenna) => (antenna?.imageIncludesDevice ? antenna.imgSrc : deviceImg)
);

export const devicesMeshApExistsSelector = (state: RootState) =>
  devicesMeshApExistsGetter(devicesDeviceMapSelector(state));

export const devicesUsedNamesSelector = createSelector(devicesCustomClientDevicesDataSelector, devicesUsedNamesGetter);

export default devicesSlice.reducer;
